import { formatEmailHtmlForDisplay } from './emailHtmlFormat';

/** Plaintext mit Absatzlücken in einzelne Blöcke zerlegen. */
export function splitMailPlainTextParagraphs(text: string): string[] {
  const normalized = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!normalized) return [];
  const parts = normalized.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  return parts.length > 0 ? parts : [normalized];
}

/** Marketing-/Template-Mails nutzen Tabellenlayouts — Absätze nicht umformatieren. */
function looksLikeStructuredMailHtml(html: string): boolean {
  return /<table\b/i.test(html) || /<style\b/i.test(html) || /<body\b/i.test(html);
}

/**
 * Entfernt gefährliche Teile aus &lt;style&gt;, behält Layout-CSS für HTML-Mails.
 * Verhindert u. a. Style-Breakouts und CSS-Exfiltration über @import.
 */
export function sanitizeMailStyleTags(html: string): string {
  return String(html || '').replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (_full, cssRaw: string) => {
    let css = String(cssRaw || '');
    // Break out of the style element
    css = css.replace(/<\/style/gi, '<\\/style');
    css = css.replace(/@import\b[^;]*;?/gi, '');
    css = css.replace(/expression\s*\(/gi, 'invalid(');
    css = css.replace(/behavior\s*:/gi, 'invalid:');
    css = css.replace(/-moz-binding\s*:/gi, 'invalid:');
    css = css.replace(/url\s*\(\s*['"]?\s*javascript:[^)]*\)/gi, 'url(about:blank)');
    // Avoid viewport-covering overlays leaking outside the message card.
    css = css.replace(/position\s*:\s*fixed\b/gi, 'position:absolute');
    css = css.replace(/position\s*:\s*sticky\b/gi, 'position:relative');
    return `<style>${css}</style>`;
  });
}

function safeScopeId(raw: string | undefined): string {
  const cleaned = String(raw || 'mail').replace(/[^a-zA-Z0-9_-]/g, '');
  return cleaned || 'mail';
}

/** Prefix CSS selectors so email styles only apply inside the message scope. */
export function scopeMailCss(css: string, scopeId: string): string {
  const scope = safeScopeId(scopeId);
  const attr = `[data-mail-html-scope="${scope}"]`;

  const scopeSelectorList = (selectorList: string): string =>
    selectorList
      .split(',')
      .map((sel) => {
        const s = sel.trim();
        if (!s) return '';
        if (/^(html|body|:root)$/i.test(s)) return attr;
        // body .x / html .x → scope .x
        const withoutRoot = s.replace(/^(html|body|:root)(\s+|(?=[.#\[:]))/i, '');
        return `${attr} ${withoutRoot.trim() || s}`;
      })
      .filter(Boolean)
      .join(', ');

  const scopeBlock = (block: string): string => {
    let out = '';
    let i = 0;
    while (i < block.length) {
      const nextBrace = block.indexOf('{', i);
      if (nextBrace < 0) {
        out += block.slice(i);
        break;
      }
      const prelude = block.slice(i, nextBrace).trim();
      // Find matching closing brace
      let depth = 1;
      let j = nextBrace + 1;
      while (j < block.length && depth > 0) {
        const ch = block[j];
        if (ch === '{') depth += 1;
        else if (ch === '}') depth -= 1;
        j += 1;
      }
      const inner = block.slice(nextBrace + 1, j - 1);
      if (/^@(media|supports|document|layer)\b/i.test(prelude)) {
        out += `${prelude}{${scopeBlock(inner)}}`;
      } else if (/^@/i.test(prelude)) {
        // keyframes, font-face, etc. — leave untouched
        out += `${prelude}{${inner}}`;
      } else if (prelude) {
        out += `${scopeSelectorList(prelude)}{${inner}}`;
      }
      i = j;
    }
    return out;
  };

  return scopeBlock(String(css || ''));
}

/** Move &lt;style&gt; tags into a scoped block so rules cannot restyle the app chrome. */
export function scopeMailHtml(html: string, scopeId: string): string {
  const scope = safeScopeId(scopeId);
  const styles: string[] = [];
  const withoutStyles = String(html || '').replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (_full, css: string) => {
    styles.push(String(css || ''));
    return '';
  });
  const scopedCss = styles.map((css) => scopeMailCss(css, scope)).filter(Boolean).join('\n');
  const styleTag = scopedCss ? `<style>${scopedCss}</style>` : '';
  return `<div data-mail-html-scope="${scope}">${withoutStyles}${styleTag}</div>`;
}

export function prepareMailHtmlForPreview(
  html: string,
  options?: { scopeId?: string },
): string {
  const raw = String(html || '');
  if (!raw.trim()) return '';
  const scopeId = safeScopeId(options?.scopeId);
  // Vollständige HTML-Dokumente in einen Fragment-Wrapper überführen
  const fragment = raw
    .replace(/<\/?(?:html|head|meta|link|xml|!doctype)[^>]*>/gi, '')
    .replace(/<body\b([^>]*)>/gi, (_full, attrs: string) => {
      const kept = String(attrs || '').match(
        /\s(?:style|align|class|bgcolor|width)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi,
      );
      return `<div${kept ? kept.join('') : ''}>`;
    })
    .replace(/<\/body>/gi, '</div>');
  const withStyles = sanitizeMailStyleTags(fragment);
  const prepared = looksLikeStructuredMailHtml(raw)
    ? withStyles
    : formatEmailHtmlForDisplay(withStyles);
  return scopeMailHtml(prepared, scopeId);
}

/**
 * Preview-Styles: Tabellenlayouts von HTML-Mails nicht zerstören
 * (kein display:block auf table, kein max-w auf alle Nachfahren).
 * Link-/Textfarben nicht erzwingen — sonst weichen Template-Mails von Gmail ab.
 *
 * Absenderseitiges HTML ist für helle Hintergründe gebaut, deshalb bleibt der Body
 * eine `theme-light`-Insel. Rahmen und Radius rahmen sie im Dark Mode als Dokument ein,
 * statt sie als randlose weiße Fläche in die dunkle Oberfläche zu schneiden.
 */
export const MAIL_HTML_PREVIEW_CLASSES =
  'theme-light min-w-0 max-w-none overflow-x-auto break-words rounded-lg border border-app-border bg-white p-3 text-slate-900 ' +
  '[&_img]:inline-block [&_img]:h-auto [&_img]:max-w-full [&_img]:object-contain [&_img[src*="/attachments/download"]]:cursor-pointer ' +
  '[&_[align=center]]:text-center [&_table[align=center]]:mx-auto [&_center]:mx-auto [&_center]:block [&_center]:text-center ' +
  '[&_a[href*="/attachments/download"]]:cursor-pointer ' +
  '[&_table]:max-w-full [&_pre]:whitespace-pre-wrap [&_pre]:break-words';

/** Zusätzliche Lesbarkeit für einfache (nicht template-basierte) HTML-Mails. */
export const MAIL_HTML_PREVIEW_SIMPLE_CLASSES =
  'text-sm text-slate-800 ' +
  '[&_ul]:mb-3 [&_ol]:mb-3 [&_ul]:pl-5 [&_ol]:pl-5 [&_ul]:list-disc [&_ol]:list-decimal ' +
  '[&_a]:text-pipedrive-blue [&_a]:underline';

export function mailHtmlPreviewClassName(html: string): string {
  const base = MAIL_HTML_PREVIEW_CLASSES;
  if (looksLikeStructuredMailHtml(html)) return base;
  return `${base} ${MAIL_HTML_PREVIEW_SIMPLE_CLASSES}`;
}
